import {
  getSPDataWithoutParams,
  putSPDataWithoutParams,
} from './services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

/**
 * Fetch SupportAgent profile for the authenticated user
 * @returns {Promise<Object|null>} SupportAgent profile object or null if error
 * @throws {Error} If the user doesn't have a SupportAgent profile (code 4002)
 */
export async function getSupportAgentProfile() {
  try {
    const response = await getSPDataWithoutParams('/api/secure/cc/support/support-agent/profile')
    
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const data = getDataMapFromCommonResponse(response)
      return data?.supportAgent || response?.data?.payload || null
    }
    
    // Check for INVALID_USER_ID error (4002)
    if (response?.data?.code === 4002) {
      throw new Error('INVALID_USER_ID')
    }
    
    return null
  } catch (error) {
    console.error('Error fetching SupportAgent profile:', error)
    // Re-throw INVALID_USER_ID errors
    if (error.message === 'INVALID_USER_ID') {
      throw error
    }
    return null
  }
}

/**
 * Update SupportAgent profile
 * @param {Object} profileData - Profile update data
 * @param {string} [profileData.screenName] - Friendly display name
 * @param {string} [profileData.supportEmail] - Support email address
 * @param {string} [profileData.supportPhoneNumber] - Support phone number
 * @param {string[]} [profileData.supportMode] - Array of support modes: 'whatsapp', 'email', 'sms', 'phone'
 * @returns {Promise<Object|null>} Updated SupportAgent profile or null if error
 */
export async function updateSupportAgentProfile(profileData) {
  try {
    // Sanitize supportMode - convert to lowercase and filter valid values
    const validModes = ['whatsapp', 'email', 'sms', 'phone']
    const sanitizedData = {
      ...profileData,
      supportMode: profileData.supportMode
        ? profileData.supportMode
            .map((mode) => mode.toLowerCase())
            .filter((mode) => validModes.includes(mode))
        : undefined,
    }

    // Remove undefined values and convert blank strings to null
    const cleanedData = Object.keys(sanitizedData).reduce((acc, key) => {
      const value = sanitizedData[key]
      if (value !== undefined) {
        acc[key] = value === '' ? null : value
      }
      return acc
    }, {})

    const response = await putSPDataWithoutParams(
      '/api/secure/cc/support/support-agent/profile',
      cleanedData,
    )

    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const data = getDataMapFromCommonResponse(response)
      return data?.supportAgent || response?.data?.payload || null
    }

    return null
  } catch (error) {
    console.error('Error updating SupportAgent profile:', error)
    throw error
  }
}
